import { Asset } from 'expo-asset';
import { Link } from 'expo-router';
import { Fragment } from 'react';

import { Fonts } from '@/constants/theme';

import type { CityHeroProps } from './city-hero.types';
import css from './city-hero.module.css';

// Web: a plain <img>, not expo-image — same reasoning as hero-search.web.tsx
// and card-image.web.tsx. This hero is the city page's LCP candidate, and
// expo-image only inserts its <img> after hydration, so a raw <img> is what
// lets the preload scanner discover it in the initial document.
//
// srcSet mirrors hero-search.web.tsx's two-size approach so narrow viewports
// don't pay for the 1024px desktop asset. The hero is boxed inside the
// page's content column (MaxContentWidth minus 16px gutters), so `sizes`
// tracks that width rather than 100vw.
export function CityHero({
  image,
  imageSm,
  alt,
  title,
  crumbs,
  chips,
  onChipPress,
}: CityHeroProps) {
  const uri = Asset.fromModule(image).uri;
  const smUri = Asset.fromModule(imageSm).uri;
  return (
    <div className={css.hero}>
      <img
        src={uri}
        srcSet={`${smUri} 750w, ${uri} 1024w`}
        sizes="(min-width: 1280px) 1248px, calc(100vw - 32px)"
        alt={alt}
        className={css.img}
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />
      <div className={css.shade} />

      <nav className={css.crumbs} aria-label="Breadcrumb">
        <ol className={css.crumbList}>
          {crumbs.map((crumb) => (
            <Fragment key={crumb.label}>
              <li>
                <Link href={crumb.href} style={{ color: 'inherit' }}>
                  {crumb.label}
                </Link>
              </li>
              <li className={css.crumbSep} aria-hidden>
                ›
              </li>
            </Fragment>
          ))}
          <li aria-current="page">{alt}</li>
        </ol>
      </nav>

      <div className={css.content}>
        <h1 className={css.title} style={{ fontFamily: Fonts?.serif }}>
          {title}
        </h1>
        {chips.length > 0 ? (
          <div className={css.chips}>
            {chips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                className={css.chip}
                onClick={() => onChipPress(chip.id)}>
                <chip.icon size={16} color="#fff" />
                {chip.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
